// Fee types

export type FeeType = "NEW" | "USED";

export interface Fee {
  id?: string;
  name: string;
  amount: number | null;
  removed: boolean;
  type: FeeType;
}

// API Request/Response types

export interface GetFeesRequest {
  tenantType: string;
  tenantId: string;
}

export interface GetFeesResponse {
  fees: Fee[];
}

export interface SaveFeesRequest {
  tenantType: string;
  tenantId: string;
  fees: Fee[];
  tenantUser: string;
}

export type SaveFeesResponse =
  | { success: true; fees: Fee[] }
  | { success: false; error: "CONFLICT" };

// Config types

export type PriceAfterFeesAttribute =
  | "askingPrice"
  | "internetPrice"
  | "invoicePrice"
  | "msrp"
  | "retailValue"
  | "salePrice"
  | "wholesalePrice";

export interface FeeManagerConfig {
  NEW_applyOnInventoryItemSave: boolean;
  NEW_saveTotalFees: boolean;
  NEW_saveItemizedFees: boolean;
  NEW_savePriceAfterFees: boolean;
  NEW_priceAfterFeesInputAttribute: PriceAfterFeesAttribute;
  USED_applyOnInventoryItemSave: boolean;
  USED_saveTotalFees: boolean;
  USED_saveItemizedFees: boolean;
  USED_savePriceAfterFees: boolean;
  USED_priceAfterFeesInputAttribute: PriceAfterFeesAttribute;
  [key: string]: unknown;
}

export type SaveConfigResponse =
  | { success: true; config: FeeManagerConfig }
  | { success: false; error: "CONFLICT" };

export interface GetConfigRequest {
  tenantType: string;
  tenantId: string;
}

export interface SaveConfigRequest {
  tenantType: string;
  tenantId: string;
  config: FeeManagerConfig;
  tenantUser: string;
}

// Configuration schema types

type FeeManagerConfigKnownFields = Pick<
  FeeManagerConfig,
  | "NEW_applyOnInventoryItemSave"
  | "NEW_saveTotalFees"
  | "NEW_saveItemizedFees"
  | "NEW_savePriceAfterFees"
  | "NEW_priceAfterFeesInputAttribute"
  | "USED_applyOnInventoryItemSave"
  | "USED_saveTotalFees"
  | "USED_saveItemizedFees"
  | "USED_savePriceAfterFees"
  | "USED_priceAfterFeesInputAttribute"
>;

export type FeeManagerConfigFieldKey = keyof FeeManagerConfigKnownFields;

export type FeeManagerConfigFieldSchema<TValue> =
  | {
      type: "boolean";
      defaultValue: boolean;
      label: string;
    }
  | {
      type: "select";
      defaultValue: TValue;
      label: string;
      options: { label: string; value: TValue }[];
      disabled?: (config: FeeManagerConfig) => boolean;
    }
  | {
      type: "input";
      inputType: "text" | "number";
      defaultValue: TValue;
      label: string;
    };

export type FeeManagerTenantSchema = {
  [TKey in FeeManagerConfigFieldKey]: FeeManagerConfigFieldSchema<
    FeeManagerConfigKnownFields[TKey]
  >;
};

export const getConfigDefaults = (
  schema: FeeManagerTenantSchema
): FeeManagerConfig =>
  Object.fromEntries(
    (Object.keys(schema) as FeeManagerConfigFieldKey[]).map(key => [
      key,
      schema[key].defaultValue
    ])
  ) as FeeManagerConfig;

export const DEFAULT_FEE_MANAGER_CONFIG: FeeManagerConfig = {
  NEW_applyOnInventoryItemSave: true,
  NEW_saveTotalFees: false,
  NEW_saveItemizedFees: false,
  NEW_savePriceAfterFees: false,
  NEW_priceAfterFeesInputAttribute: "invoicePrice",
  USED_applyOnInventoryItemSave: true,
  USED_saveTotalFees: false,
  USED_saveItemizedFees: false,
  USED_savePriceAfterFees: false,
  USED_priceAfterFeesInputAttribute: "invoicePrice"
};

export const migrateConfig = (
  raw: Record<string, unknown>
): FeeManagerConfig => {
  if ("NEW_applyOnInventoryItemSave" in raw)
    return { ...DEFAULT_FEE_MANAGER_CONFIG, ...raw } as FeeManagerConfig;

  if ("applyOnInventoryItemSave" in raw) {
    return {
      NEW_applyOnInventoryItemSave: raw["applyOnInventoryItemSave"] as boolean,
      NEW_saveTotalFees: raw["NEW_emitTotalFees"] as boolean,
      NEW_saveItemizedFees: raw["NEW_emitItemizedFees"] as boolean,
      NEW_savePriceAfterFees: raw["NEW_emitPriceAfterFees"] as boolean,
      NEW_priceAfterFeesInputAttribute: raw[
        "priceAfterFeesInputAttribute"
      ] as PriceAfterFeesAttribute,
      USED_applyOnInventoryItemSave: raw["applyOnInventoryItemSave"] as boolean,
      USED_saveTotalFees: raw["USED_emitTotalFees"] as boolean,
      USED_saveItemizedFees: raw["USED_emitItemizedFees"] as boolean,
      USED_savePriceAfterFees: raw["USED_emitPriceAfterFees"] as boolean,
      USED_priceAfterFeesInputAttribute: raw[
        "priceAfterFeesInputAttribute"
      ] as PriceAfterFeesAttribute
    };
  }

  return { ...DEFAULT_FEE_MANAGER_CONFIG };
};

// Fetch interface for hooks

export interface FeeManagerFetchInterface {
  tenantType: string;
  tenantId: string;
  disabled?: boolean;
}

export interface FeeManagerFetchWriteInterface {
  tenantType: string;
  tenantId: string;
  afterSuccess?: ((data: unknown) => void) | string;
}

// Type guards

export function isFee(object: unknown): object is Fee {
  if (!object || typeof object !== "object") return false;
  const obj = object as Record<string, unknown>;
  return (
    (obj.id === undefined || typeof obj.id === "string") &&
    typeof obj.name === "string" &&
    (typeof obj.amount === "number" || obj.amount === null) &&
    typeof obj.removed === "boolean" &&
    (obj.type === "NEW" || obj.type === "USED")
  );
}

export function isGetFeesResponse(object: unknown): object is GetFeesResponse {
  if (!object || typeof object !== "object") return false;
  const obj = object as Record<string, unknown>;
  return Array.isArray(obj.fees);
}
